/* eslint-disable @typescript-eslint/camelcase */

import { Status, NonClientError } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import useQueryParameters from 'hooks/useQueryParameters';
import { useCallback, useState } from 'react';
import { offerResponse, OfferResponseType } from 'fmc-sdk';
import useFmcHost from 'hooks/useFmcHost';
import log from 'loglevel';
import catchOnly from 'utils/catchOnly';
import { useSelector } from 'react-redux';
import { selectCreditApplication } from 'state/creditApplication';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';

type OfferMadeHook = () => [
  (choice: OfferMadeEnum) => Promise<void>,
  Status,
  boolean,
];

export enum OfferMadeEnum {
  Yes = 'yes',
  No = 'no',
  Abandoned = 'abandoned',
  NotMe = 'not_me',
  PrescreenNo = 'prescreen_no',
}

const OfferMadeEnumMap = {
  [OfferMadeEnum.Yes]: OfferResponseType.Yes,
  [OfferMadeEnum.No]: OfferResponseType.No,
  [OfferMadeEnum.Abandoned]: OfferResponseType.Abandoned,
  [OfferMadeEnum.NotMe]: OfferResponseType.NotMe,
  [OfferMadeEnum.PrescreenNo]: OfferResponseType.PrescreenNo,
};

export class OfferMadeError extends Error {}

const PATH = '/api/made_offer';

const useOfferMade: OfferMadeHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const fmcDispatch = useFmcHost();
  const {
    prescreenId,
    storeNumber,
    mockMO: mockResponse,
  } = useQueryParameters();
  const { correlationData, customerNumber } = useSelector(
    selectCreditApplication,
  );

  const overrideAtributes = useApplicationBasedOverrides();

  const sendOfferMade = useCallback(
    async (choice: OfferMadeEnum) => {
      if (!prescreenId) {
        throw new Error('No Prescreen ID presented.');
      }
      try {
        setError(false);
        const response = await fetch(PATH, {
          method: 'POST',
          body: {
            ...overrideAtributes,
            mockResponse,
            prescreenId,
            storeNumber,
            presentationInd: choice,
            correlationData,
            customerNumber,
          },
        });
        fmcDispatch(offerResponse(OfferMadeEnumMap[choice]));
        if (!response.ok) {
          setError(true);
          log.warn('/api/made_offer: Error received from server.');
        }
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          setError(true);
          log.warn('/api/made_offer: Error received from server.');
          fmcDispatch(offerResponse(OfferMadeEnumMap[choice]));
        }
      }
    },
    [
      prescreenId,
      fetch,
      overrideAtributes,
      mockResponse,
      storeNumber,
      correlationData,
      customerNumber,
      fmcDispatch,
    ],
  );

  return [sendOfferMade, status, error];
};

export default useOfferMade;
